import { useMutation, useQuery } from '@tanstack/react-query'
import { ActionBar, Dialog, Text } from '@viapeople/components'
import RatingAverage from '@viapeople/components/src/icons/RatingAverageIcon'
import { useState } from 'react'
import { Outlet, useNavigate, useOutletContext, useParams } from 'react-router-dom'
import { type Column, GenericTable } from '../../../../components/GenericTable'
import { useToast } from '../../../../utils/ToastContext'
import { queryClient, trpc } from '../../../../utils/trpc'
import type { Organization } from '../../types'
import type { CycleReviewersUnsolicited, CycleReviewersUnsolicitedInput } from './types'

const columns: Column<CycleReviewersUnsolicited>[] = [
	{
		field: 'id',
		label: 'Allowed to leave feedback',
		colSpan: 1,
		sortable: false,
		render: (cycleReviewersUnsolicited: CycleReviewersUnsolicited) => {
			const names = [
				...cycleReviewersUnsolicited.reviewersPopulation.roles.map((role) => role.displayName),
				...cycleReviewersUnsolicited.reviewersPopulation.whitelistedUsers.map(
					(user) => `${user.firstName} ${user.lastName}`,
				),
			]
			return (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{names.join(', ')}
				</Text>
			)
		},
	},
]

export function CycleReviewersUnsolicitedTab() {
	const { organization } = useOutletContext<{ organization: Organization }>()
	const { cycleId } = useParams<{ cycleId: string }>()
	const navigate = useNavigate()
	const [selectedUnsolicitedReviewersToDelete, setSelectedUnsolicitedReviewersToDelete] = useState<
		CycleReviewersUnsolicited[]
	>([])
	const toast = useToast()
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
	const deleteUnsolicitedReviewersMutation = useMutation(
		trpc.admin.cycles.reviewers.unsolicited.delete.mutationOptions({
			onSuccess: () => {
				toast.showToast('Unsolicited reviewers deleted successfully', 'info')
			},
			onError: (error) => {
				toast.showToast(error.message || 'Failed to delete unsolicited reviewers', 'error')
			},
			onSettled: () => {
				queryClient.invalidateQueries({
					queryKey: trpc.admin.cycles.reviewers.unsolicited.search.queryKey({ cycleId: cycleId as string }),
					exact: false,
					refetchType: 'all',
				})
			},
		}),
	)

	const { data: cycle } = useQuery(
		trpc.admin.cycles.getById.queryOptions({
			id: cycleId as string,
		}),
	)

	if (!cycle) {
		return null
	}

	const handleDeleteSelected = () => {
		deleteUnsolicitedReviewersMutation.mutate({
			ids: selectedUnsolicitedReviewersToDelete.map((r) => r.id),
		})
	}

	return (
		<div>
			<GenericTable<CycleReviewersUnsolicited, CycleReviewersUnsolicitedInput>
				columns={columns}
				idField='id'
				selectable
				isInternal
				showSearch
				showPagination
				queryOptionsGenerator={(options) => ({
					...trpc.admin.cycles.reviewers.unsolicited.search.queryOptions({
						...options,
						cycleId: cycle.id,
						search: options.search || '',
					}),
				})}
				emptyState={{
					icon: RatingAverage,
					title: 'Unsolicited Reviews',
					description: 'Get started by clicking below',
					actionLabel: '+ Add',
					onAction: () => navigate(`/organizations/${organization.id}/cycles/${cycle.id}/reviewers/unsolicited/create`),
				}}
				onSelectionChange={setSelectedUnsolicitedReviewersToDelete}
				onRowClick={(row) => {
					navigate(`/organizations/${organization.id}/cycles/${cycle.id}/reviewers/unsolicited/${row.id}`)
				}}
				actionButtons={[
					{
						children: 'Add',
						onClick: () =>
							navigate(`/organizations/${organization.id}/cycles/${cycle.id}/reviewers/unsolicited/create`),
					},
				]}
			/>

			{selectedUnsolicitedReviewersToDelete.length > 0 && (
				<ActionBar
					rightActions={[
						{
							label: 'Cancel',
							variant: 'secondary',
							onClick: () => setSelectedUnsolicitedReviewersToDelete([]),
						},
						{
							label: `Delete${selectedUnsolicitedReviewersToDelete.length > 0 ? ` (${selectedUnsolicitedReviewersToDelete.length})` : ''}`,
							variant: 'destructive',
							onClick: () => setIsDeleteDialogOpen(true),
						},
					]}
				/>
			)}

			<Dialog
				isOpen={isDeleteDialogOpen}
				onClose={() => setIsDeleteDialogOpen(false)}
				onConfirm={handleDeleteSelected}
				title='Delete unsolicited reviewers'
				message={`Are you sure you want to delete ${selectedUnsolicitedReviewersToDelete.length} unsolicited reviewer${selectedUnsolicitedReviewersToDelete.length === 1 ? '' : 's'}?`}
				type='delete'
				confirmLabel='Delete'
			/>

			<Outlet context={{ cycle }} />
		</div>
	)
}
